import { useCallback, useEffect, useState } from "react";
import {
  type ConversationSummary,
  deleteConversation,
  fetchConversations,
  fetchModels,
  type ModelEntry,
} from "./api";
import { ChatPane } from "./components/chat/ChatPane";
import { ModelPicker } from "./components/chat/ModelPicker";
import { type ThinkingEffort, ThinkingEffortPicker } from "./components/chat/ThinkingEffortPicker";
import { Sidebar } from "./components/sidebar/Sidebar";
import { config } from "./config";

const FALLBACK_EFFORTS: ThinkingEffort[] = ["off", "low", "medium", "high", "xhigh", "max"];

export default function App() {
  const [allEfforts, setAllEfforts] = useState<ThinkingEffort[]>(FALLBACK_EFFORTS);
  const [models, setModels] = useState<ModelEntry[]>([]);
  const [model, setModel] = useState<string>(config.defaultModel);
  const [effort, setEffort] = useState<ThinkingEffort>("medium");
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    fetchModels()
      .then((resp) => {
        setAllEfforts(resp.effort_levels);
        setModels(resp.models);
      })
      .catch(() => {
        // Backend unreachable — keep the fallback model list so the picker is usable.
        setModels([
          {
            label: "Opus 4.6",
            model_id: config.opusModel,
            supported_efforts: ["off", "low", "medium", "high"],
          },
        ]);
      });
  }, []);

  const refreshConversations = useCallback(() => {
    setConversationsLoading(true);
    fetchConversations()
      .then(setConversations)
      .catch(() => setConversations([]))
      .finally(() => setConversationsLoading(false));
  }, []);

  useEffect(() => {
    refreshConversations();
  }, [refreshConversations]);

  const handleDeleteConversation = useCallback(
    (id: string) => {
      // Optimistic: remove from the list immediately so the click feels
      // responsive; if the API call fails, refresh to recover the truth.
      setConversations((prev) => prev.filter((c) => c.conversation_id !== id));
      if (activeConversationId === id) setActiveConversationId(null);
      deleteConversation(id).catch(() => refreshConversations());
    },
    [activeConversationId, refreshConversations],
  );

  const supported = models.find((m) => m.model_id === model)?.supported_efforts ?? allEfforts;
  const activeTitle =
    conversations.find((c) => c.conversation_id === activeConversationId)?.title ??
    (activeConversationId ? "Conversation" : "New chat");

  return (
    <div className={`app-shell ${sidebarOpen ? "" : "sidebar-closed"}`}>
      <Sidebar
        conversations={conversations}
        conversationsLoading={conversationsLoading}
        activeConversationId={activeConversationId}
        onSelectConversation={setActiveConversationId}
        onNewConversation={() => setActiveConversationId(null)}
        onDeleteConversation={handleDeleteConversation}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <main className="app-main">
        <div className="topbar no-print">
          <button
            type="button"
            className="topbar-toggle"
            onClick={() => setSidebarOpen((o) => !o)}
            aria-label="Toggle sidebar"
            title="Toggle sidebar"
          >
            ☰
          </button>
          <div className="topbar-title">{activeTitle}</div>
          <div className="controls">
            <ModelPicker value={model} onChange={setModel} models={models} />
            <ThinkingEffortPicker
              value={effort}
              onChange={setEffort}
              all={allEfforts}
              supported={supported}
            />
          </div>
        </div>
        <ChatPane
          model={model}
          effort={effort}
          conversationId={activeConversationId}
          onConversationCreated={(id) => {
            setActiveConversationId(id);
            refreshConversations();
          }}
          onConversationChanged={refreshConversations}
        />
      </main>
    </div>
  );
}
